import _ from 'lodash'
import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../base/customHooks/componentHOC'
import { translate } from '../../../base/localization'
import { IException } from '../../../base/models'
import IPButton from '../../../components/IPButton'
import IPSelectBox, { ISelectOption } from '../../../components/IPSelectBox/IPSelectBox'
import Translations from '../../../translations'
import '../Openbox.scss'
import { IOpenBox } from '../models'
import { GetOpenBoxByUserIdApi } from '../services/getOpenBoxByUser'
import { ReadOpenBoxApi } from '../services/readOpenboxApi'

interface OpenboxState {
  accordionActiveKey: string
  openBoxes: IOpenBox[]
  monthsOption?: ISelectOption
  yearsOption?: ISelectOption
  years: ISelectOption[]
  filterOpenBoxes: IOpenBox[]
}

const ListItem = props => {
  const [showItem, setShowItem] = useState(false)

  return (
    <>
      <tr
        role='row'
        className='odd'
        onClick={() => {
          props.onOpenboxAccordionChange(props.openBox.Id, props.openBox.Enable)
          setShowItem(!showItem)
        }}
        style={{
          fontWeight: props.openBox.Enable ? '700' : '400',
          color: props.openBox.Enable ? 'black' : '#555A6C',
          fontSize: '1.3rem',
          cursor: 'pointer',
        }}
      >
        <td style={{ width: '20px' }}>
          {!props.openBox.Enable ? (
            <img src='/images/bell-regular.svg' alt='' width={20} />
          ) : (
            <img src='/images/bell-regular_kirmizi-01.svg' alt='' width={20} />
          )}
        </td>
        <td>{new Date(props.openBox.StartDate).toLocaleString('tr-TR')}</td>
        <td>{new Date(props.openBox.EndDate).toLocaleString('tr-TR')}</td>
        <td style={{ width: 300 }}>{props.openBox.Topic}</td>
      </tr>
      {showItem && (
        <tr role='row' className='odd'>
          <td colSpan={4}>
            <div
              className='list-inline mb-0'
              style={{
                fontSize: '1.3rem',
                color: 'rgb(97, 97, 97)',
              }}
            >
              {!props.openBox.Image && props.openBox.Link ? (
                <a
                  className={
                    props.openBox.Link.includes('https://www.indexgruppazar.com') ? 'link_id' : 'outbound_link'
                  }
                  href={props.openBox.Link}
                  style={{ cursor: 'pointer' }}
                  target='_blank'
                  rel='noreferrer'
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: props.openBox.Description ? props.openBox.Description : '',
                    }}
                  ></div>
                </a>
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: props.openBox.Description ? props.openBox.Description : '',
                  }}
                ></div>
              )}

              {props.openBox.Image ? (
                props.openBox.Link ? (
                  <a
                    className={
                      props.openBox.Link.includes('https://www.indexgruppazar.com') ? 'link_id' : 'outbound_link'
                    }
                    href={props.openBox.Link}
                    style={{ cursor: 'pointer' }}
                    target='_blank'
                    rel='noreferrer'
                  >
                    <img src={props.openBox.ImageUrl} alt={props.openBox.Image} />
                  </a>
                ) : (
                  <img src={props.openBox.ImageUrl} alt={props.openBox.Image} />
                )
              ) : (
                ''
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function Openbox(props: BaseComponentWithContextProps) {
  const months: ISelectOption[] = [
    { value: 0, label: 'Ocak' },
    { value: 1, label: 'Şubat' },
    { value: 2, label: 'Mart' },
    { value: 3, label: 'Nisan' },
    { value: 4, label: 'Mayıs' },
    { value: 5, label: 'Haziran' },
    { value: 6, label: 'Temmuz' },
    { value: 7, label: 'Ağustos' },
    { value: 8, label: 'Eylül' },
    { value: 9, label: 'Ekim' },
    { value: 10, label: 'Kasım' },
    { value: 11, label: 'Aralık' },
  ]

  const getInitialState = (): OpenboxState => {
    return {
      accordionActiveKey: '',
      openBoxes: [],
      years: [],
      filterOpenBoxes: [],
    }
  }

  const t = translate(Translations)

  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<OpenboxState>(intialState)

  const getTime = (date?: Date | string) => {
    const time = date ? new Date(date).getTime() : NaN
    return isNaN(time) ? undefined : time
  }

  // Duyurunun [StartDate, EndDate] aralığı [first, end) dönemiyle kesişiyorsa duyuru o döneme aittir
  const overlaps = (box: IOpenBox, first: Date, end: Date) => {
    const start = getTime(box.StartDate) ?? getTime(box.EndDate)
    const finish = getTime(box.EndDate) ?? start
    if (start === undefined || finish === undefined) return false
    return start < end.getTime() && finish >= first.getTime()
  }

  // Yıl ve ay ayrı ayrı opsiyoneldir: ikisi de yoksa tüm zamanlar, yalnız ay seçiliyse her yılın o ayı
  const filterByPeriod = (boxes: IOpenBox[], year?: number, month?: number) => {
    const filtered = boxes.filter(box => {
      if (year === undefined && month === undefined) return true
      if (month === undefined) return overlaps(box, new Date(year!, 0, 1), new Date(year! + 1, 0, 1))
      if (year !== undefined) return overlaps(box, new Date(year, month, 1), new Date(year, month + 1, 1))
      const start = getTime(box.StartDate) ?? getTime(box.EndDate)
      const finish = getTime(box.EndDate) ?? start
      if (start === undefined || finish === undefined) return false
      for (let y = new Date(start).getFullYear(); y <= new Date(finish).getFullYear(); y++) {
        if (overlaps(box, new Date(y, month, 1), new Date(y, month + 1, 1))) return true
      }
      return false
    })
    return filtered.sort((a, b) => (getTime(b.StartDate) ?? 0) - (getTime(a.StartDate) ?? 0) || b.Id - a.Id)
  }

  const getOpenBoxes = () => {
    props.showLoading()
    const getOpenBoxApi: GetOpenBoxByUserIdApi = new GetOpenBoxByUserIdApi({})
    getOpenBoxApi
      .getOpenBox()
      .then((res: IOpenBox[]) => {
        const year = new Date().getFullYear()
        const yearSet = new Set<number>([year])
        res.forEach(box => {
          ;[box.StartDate, box.EndDate].forEach(date => {
            const time = getTime(date)
            if (time !== undefined) yearSet.add(new Date(time).getFullYear())
          })
        })
        const years: ISelectOption[] = Array.from(yearSet)
          .sort((a, b) => b - a)
          .map(y => ({ value: y, label: y }))
        setState({
          openBoxes: res,
          years,
          filterOpenBoxes: filterByPeriod(res, year),
          yearsOption: { label: year, value: year },
          monthsOption: undefined,
        })
        props.hideLoading()
      })
      .catch((err: IException) => {
        props.hideLoading()
        Swal.fire({
          // position: 'top-end',
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
          // timer: 1500
        })
      })
  }

  useEffect(() => {
    getOpenBoxes()
  }, [])

  const onOpenboxAccordionChange = (activeKey: string, isEnabled?: boolean) => {
    setState({ accordionActiveKey: activeKey === state.accordionActiveKey ? '' : activeKey })

    if (activeKey !== state.accordionActiveKey && isEnabled) {
      const api: ReadOpenBoxApi = new ReadOpenBoxApi({})

      api
        .readOpenBox(Number(activeKey))
        .then((res: boolean) => {
          if (res) {
            const tmpOpenboxes = _.clone(state.openBoxes)
            let tmpOpenBoxIndex = tmpOpenboxes.findIndex((box: IOpenBox) => {
              return Number(activeKey) === box.Id
            })

            if (tmpOpenboxes && tmpOpenBoxIndex > -1) {
              tmpOpenboxes[tmpOpenBoxIndex].Enable = false
            }

            setState({ openBoxes: tmpOpenboxes })
          }
        })
        .catch((err: IException) => {
          Swal.fire({
            // position: 'top-end',
            icon: 'error',
            title: err.description,
            showConfirmButton: true,
            allowOutsideClick: false,
            // timer: 1500
          })
        })
    }
  }

  const dateChange = () => {
    const year = state.yearsOption ? Number(state.yearsOption.value) : undefined
    const month = state.monthsOption ? Number(state.monthsOption.value) : undefined
    setState({ filterOpenBoxes: filterByPeriod(state.openBoxes, year, month) })
  }

  return (
    <div className='openbox'>
      <div className='container'>
        <div className='row'>
          <p style={{ fontSize: '2rem', fontWeight: 500, color: '#616161' }}>{t('pages.openbox.header')}</p>
          <div className='col-12 col-lg-12'>
            <div className='row'>
              <div className='col-md-2'>
                <IPSelectBox
                  isClearable
                  className='UserType'
                  name='UserType'
                  placeholder='Ay (tümü)'
                  onChangeSingle={(option: ISelectOption) => {
                    setState({ monthsOption: option ?? undefined })
                  }}
                  options={months}
                  value={state.monthsOption}
                />
              </div>

              <div className='col-md-2'>
                <IPSelectBox
                  isClearable
                  className='UserType'
                  name='UserType'
                  placeholder='Yıl (tümü)'
                  onChangeSingle={(option: ISelectOption) => {
                    setState({ yearsOption: option ?? undefined })
                  }}
                  options={state.years}
                  value={state.yearsOption}
                />
              </div>
              <div className='col-md-2'>
                <IPButton className='w-25' text={t('pages.openbox.show')} onClick={dateChange} />
              </div>
            </div>

            <hr />

            <div className='row'>
              <div id='datatable_wrapper' className='dataTables_wrapper dt-bootstrap4'>
                <div className='row'>
                  <div className='col-sm-12'>
                    <div className='table-responsive'>
                      <table
                        id='datatable'
                        className='table table-bordered table-hover dataTable'
                        style={{ minWidth: 800 }}
                        role='grid'
                        aria-describedby='datatable_info'
                      >
                        <thead>
                          <tr role='row' className='odd'>
                            <th style={{ width: '20px' }}>{t('pages.openbox.status')}</th>
                            <th style={{ width: '100px' }}>{t('pages.openbox.startDate')}</th>
                            <th style={{ width: '100px' }}>{t('pages.openbox.endDate')}</th>
                            <th>{t('pages.openbox.subject')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {state.filterOpenBoxes &&
                            state.filterOpenBoxes.length > 0 &&
                            state.filterOpenBoxes.map((openBox: IOpenBox) => (
                              <ListItem
                                key={openBox.Id}
                                openBox={openBox}
                                onOpenboxAccordionChange={onOpenboxAccordionChange}
                              />
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(Openbox)
